import { Suspense, type ReactElement } from "react";
import { Navigate, useParams } from "react-router-dom";
import { metuZemelapisVisible } from "./lib/launchFlags";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import Layout from "./components/Layout";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { HelmetProvider } from "react-helmet-async";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { LangProvider, useLang, useMarket } from "@/i18n";
import { IntlCurrencyProvider } from "@/contexts/IntlCurrencyContext";

// Eagerly load frequently visited pages
import Pagrindinis from "./pages/Pagrindinis";
import Knygynas from "./pages/Knygynas";

// Lazy-load remaining pages
const Apie = lazy(() => import("./pages/Apie"));
const Auth = lazy(() => import("./pages/Auth"));
const Profilis = lazy(() => import("./pages/Profilis"));
const Laiskai = lazy(() => import("./pages/Laiskai"));
const Admin = lazy(() => import("./pages/Admin"));
const NaudojimoSalygos = lazy(() => import("./pages/NaudojimoSalygos"));
const PrivatumoPolitika = lazy(() => import("./pages/PrivatumoPolitika"));
const Blogas = lazy(() => import("./pages/Blogas"));
const Aciu = lazy(() => import("./pages/Aciu"));
const Straipsnis = lazy(() => import("./pages/Straipsnis"));
const Instaliuoti = lazy(() => import("./pages/Instaliuoti"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LegacyProductRedirect = lazy(() => import("./pages/LegacyProductRedirect"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PriimtiKvietima = lazy(() => import("./pages/PriimtiKvietima"));
const DovanaGauta = lazy(() => import("./pages/DovanaGauta"));
const Pamokos = lazy(() => import("./pages/Pamokos"));
const Zemelapis = lazy(() => import("./pages/Zemelapis"));
const ZemelapisSolo = lazy(() => import("./pages/ZemelapisSolo"));
const ZemelapisPildyti = lazy(() => import("./pages/ZemelapisPildyti"));
const PorosZemelapis = lazy(() => import("./pages/PorosZemelapis"));
const PorosZemelapisKodas = lazy(() => import("./pages/PorosZemelapisKodas"));
const PorosZemelapisPildyti = lazy(() => import("./pages/PorosZemelapisPildyti"));
const Archetipas = lazy(() => import("./pages/Archetipas"));
const ArchetipasAnketa = lazy(() => import("./pages/ArchetipasAnketa"));
const ArchetipasResult = lazy(() => import("./pages/ArchetipasResult"));
const Naujienlaiskis = lazy(() => import("./pages/Naujienlaiskis"));
const PradziosZemelapis = lazy(() => import("./pages/PradziosZemelapis"));
const Nuotrauka = lazy(() => import("./pages/Nuotrauka"));
const PilnasZemelapis = lazy(() => import("./pages/PilnasZemelapis"));
const Karjera30Diena = lazy(() => import("./pages/Karjera30Diena"));
const KarjerosZemelapis = lazy(() => import("./pages/KarjerosZemelapis"));
const NaujienlaiskisLetter = lazy(() => import("./pages/NaujienlaiskisLetter"));
const NaujienlaiskisPatvirtinta = lazy(() => import("./pages/NaujienlaiskisPatvirtinta"));
const IntlMap = lazy(() => import("./pages/intl/IntlMap"));
const IntlMapSolo = lazy(() => import("./pages/intl/IntlMapSolo"));
const IntlCareerMap = lazy(() => import("./pages/intl/IntlCareerMap"));
const IntlZemelapisPildyti = lazy(() => import("./pages/intl/IntlZemelapisPildyti"));
const IntlPorosZemelapisPildyti = lazy(() => import("./pages/intl/IntlPorosZemelapisPildyti"));
const IntlCouplesMap = lazy(() => import("./pages/intl/IntlCouplesMap"));
const IntlCouplesMapCode = lazy(() => import("./pages/intl/IntlCouplesMapCode"));
const IntlArchetipas = lazy(() => import("./pages/intl/IntlArchetipas"));
const IntlArchetipasAnketa = lazy(() => import("./pages/intl/IntlArchetipasAnketa"));
const IntlArchetipasResult = lazy(() => import("./pages/intl/IntlArchetipasResult"));
const IntlLetters = lazy(() => import("./pages/intl/IntlLetters"));
const IntlLetter = lazy(() => import("./pages/intl/IntlLetter"));
const IntlAbout = lazy(() => import("./pages/intl/IntlAbout"));
const IntlTerms = lazy(() => import("./pages/intl/IntlTerms"));
const IntlPrivacy = lazy(() => import("./pages/intl/IntlPrivacy"));
const IntlProfile = lazy(() => import("./pages/intl/IntlProfile"));
const IntlThankYou = lazy(() => import("./pages/intl/IntlThankYou"));
const IntlInstall = lazy(() => import("./pages/intl/IntlInstall"));
const IntlUnsubscribe = lazy(() => import("./pages/intl/IntlUnsubscribe"));



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const LtOnlyRoute = ({ children }: { children: ReactElement }) => {
  const market = useMarket();
  return market === "intl" ? <Navigate to="/" replace /> : children;
};

const IntlPathRedirect = ({ to }: { to: string }) => {
  const market = useMarket();
  return market === "intl" ? <Navigate to={to} replace /> : <Navigate to="/" replace />;
};

const LocaleRoutes = () => {
  const { market } = useLang();

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Pagrindinis />} />
        <Route path="apie" element={market === "intl" ? <Navigate to="/about" replace /> : <Apie />} />
        <Route path="about" element={market === "intl" ? <IntlAbout /> : <Apie />} />
        <Route path="pamokos" element={<LtOnlyRoute><Pamokos /></LtOnlyRoute>} />
        <Route path="paskaitos" element={<LtOnlyRoute><Pamokos /></LtOnlyRoute>} />
        <Route path="pamokos/:slug" element={<LtOnlyRoute><Pamokos /></LtOnlyRoute>} />

        <Route path="pamokos/knygynas" element={<Navigate to="/knygynas" replace />} />
        <Route path="pamokos/knygynas/:slug" element={<KnygynasLegacyRedirect />} />
        <Route path="pamokosnew" element={<Navigate to="/pamokos" replace />} />
        <Route path="pamokosnew/*" element={<Navigate to="/pamokos" replace />} />
        <Route path="auth" element={<Auth />} />
        <Route path="profilis" element={market === "intl" ? <Navigate to="/profile" replace /> : <Profilis />} />
        <Route path="profile" element={market === "intl" ? <IntlProfile /> : <Profilis />} />
        <Route path="laiskai" element={market === "intl" ? <Navigate to="/letters" replace /> : <Laiskai />} />
        <Route path="admin" element={<Admin />} />
        <Route path="naudojimosi-salygos" element={market === "intl" ? <Navigate to="/terms" replace /> : <NaudojimoSalygos />} />
        <Route path="terms" element={market === "intl" ? <IntlTerms /> : <NaudojimoSalygos />} />
        <Route path="privatumo-politika" element={market === "intl" ? <Navigate to="/privacy" replace /> : <PrivatumoPolitika />} />
        <Route path="privacy" element={market === "intl" ? <IntlPrivacy /> : <PrivatumoPolitika />} />
        <Route path="knygynas" element={<LtOnlyRoute><Knygynas /></LtOnlyRoute>} />
        <Route path="knygynas/:slug" element={<LtOnlyRoute><Knygynas /></LtOnlyRoute>} />
        <Route path="blogas" element={<LtOnlyRoute><Blogas /></LtOnlyRoute>} />
        <Route path="blogas/:slug" element={<LtOnlyRoute><Straipsnis /></LtOnlyRoute>} />
        <Route path="aciu" element={market === "intl" ? <Navigate to="/thank-you" replace /> : <Aciu />} />
        <Route path="thank-you" element={market === "intl" ? <IntlThankYou /> : <Aciu />} />
        <Route path="instaliuoti" element={market === "intl" ? <Navigate to="/install" replace /> : <Instaliuoti />} />
        <Route path="install" element={market === "intl" ? <IntlInstall /> : <Instaliuoti />} />
        <Route path="preke/:slug" element={<LtOnlyRoute><LegacyProductRedirect /></LtOnlyRoute>} />
        <Route path="unsubscribe" element={market === "intl" ? <IntlUnsubscribe /> : <Unsubscribe />} />
        <Route path="priimti-kvietima" element={market === "intl" ? <Navigate to="/accept-invitation" replace /> : <PriimtiKvietima />} />
        <Route path="accept-invitation" element={<PriimtiKvietima />} />
        <Route path="dovana-gauta" element={market === "intl" ? <Navigate to="/gift-received" replace /> : <DovanaGauta />} />
        <Route path="gift-received" element={<DovanaGauta />} />
        <Route path="zemelapis" element={market === "intl" ? <Navigate to="/map" replace /> : <Zemelapis />} />
        <Route path="map" element={market === "intl" ? <IntlMap /> : <Navigate to="/zemelapis" replace />} />
        <Route path="zemelapis/solo" element={market === "intl" ? <Navigate to="/map/solo" replace /> : <ZemelapisSolo />} />
        <Route path="map/solo" element={market === "intl" ? <IntlMapSolo /> : <Navigate to="/zemelapis/solo" replace />} />
        <Route path="zemelapis/pildyti/:sessionId" element={market === "intl" ? <IntlPathRedirect to="/map" /> : <ZemelapisPildyti />} />
        <Route path="map/fill/:sessionId" element={market === "intl" ? <IntlZemelapisPildyti /> : <Navigate to="/zemelapis" replace />} />
        <Route path="poros-zemelapis" element={market === "intl" ? <Navigate to="/couples-map" replace /> : <PorosZemelapis />} />
        <Route path="couples-map" element={market === "intl" ? <IntlCouplesMap /> : <Navigate to="/poros-zemelapis" replace />} />
        <Route path="poros-zemelapis/kodas" element={market === "intl" ? <Navigate to="/couples-map/code" replace /> : <PorosZemelapisKodas />} />
        <Route path="couples-map/code" element={market === "intl" ? <IntlCouplesMapCode /> : <Navigate to="/poros-zemelapis/kodas" replace />} />
        <Route path="poros-zemelapis/pildyti/:pairId" element={market === "intl" ? <IntlPathRedirect to="/couples-map" /> : <PorosZemelapisPildyti />} />
        <Route path="couples-map/fill/:pairId" element={market === "intl" ? <IntlPorosZemelapisPildyti /> : <Navigate to="/poros-zemelapis" replace />} />
        <Route path="archetipas" element={market === "intl" ? <Navigate to="/archetype" replace /> : <Archetipas />} />
        <Route path="archetype" element={market === "intl" ? <IntlArchetipas /> : <Navigate to="/archetipas" replace />} />
        <Route path="archetipas/anketa" element={market === "intl" ? <Navigate to="/archetype/details" replace /> : <ArchetipasAnketa />} />
        <Route path="archetype/details" element={market === "intl" ? <IntlArchetipasAnketa /> : <Navigate to="/archetipas/anketa" replace />} />
        <Route path="archetipas/:slug" element={market === "intl" ? <Navigate to="/archetype" replace /> : <ArchetipasResult />} />
        <Route path="archetype/:slug" element={market === "intl" ? <IntlArchetipasResult /> : <Navigate to="/archetipas" replace />} />

        <Route path="santykiu-pradzios-zemelapis" element={market === "intl" ? <Navigate to="/" replace /> : <PradziosZemelapis />} />
        <Route path="nuotrauka" element={metuZemelapisVisible() ? <Nuotrauka /> : <Navigate to="/" replace />} />
        <Route path="pilnas" element={metuZemelapisVisible() ? <PilnasZemelapis /> : <Navigate to="/" replace />} />
        <Route path="pilnas/knyga" element={metuZemelapisVisible() ? <PilnasZemelapis /> : <Navigate to="/" replace />} />
        <Route path="karjeros-zemelapis/30-diena" element={<Karjera30Diena />} />
        <Route path="karjeros-zemelapis" element={market === "intl" ? <Navigate to="/map/career" replace /> : <KarjerosZemelapis />} />
        <Route path="map/career" element={market === "intl" ? <IntlCareerMap /> : <Navigate to="/karjeros-zemelapis" replace />} />

        <Route path="naujienlaiskis" element={market === "intl" ? <Navigate to="/letters" replace /> : <Naujienlaiskis />} />
        <Route path="letters" element={market === "intl" ? <IntlLetters /> : <Naujienlaiskis />} />
        <Route path="naujienlaiskis/patvirtinta" element={market === "intl" ? <Navigate to="/letters" replace /> : <NaujienlaiskisPatvirtinta />} />
        <Route path="naujienlaiskis/:slug" element={market === "intl" ? <Navigate to="/letters" replace /> : <NaujienlaiskisLetter />} />
        <Route path="letters/:slug" element={market === "intl" ? <IntlLetter /> : <NaujienlaiskisLetter />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const KnygynasLegacyRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/knygynas/${slug ?? ""}`} replace />;
};

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <LangProvider>
              <IntlCurrencyProvider>
                <Suspense fallback={<div className="flex-1 flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>}>
                  <LocaleRoutes />
                </Suspense>
              </IntlCurrencyProvider>
            </LangProvider>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
